/**
 *
 *
 *
 *
 */
import type { Variation } from '@shared/types/types';
import type { CrocotileClient } from '@tilelist/clients/CrocotileClient';
import { isProductItem } from '@tilelist/domain/composition/TileListitem.ts';
import type { ProductItem, TileListItem } from '@tilelist/domain/composition/TileListitem.ts';
import { sortedColorsWithBestVariationFirst } from '@tilelist/domain/feature-visibility/ColorSorting.ts';

export class VariationLoadingService {
    constructor(private client: CrocotileClient) {}

    /**
 *
 *
 *
 *
 *
 *
 *
 */
    async loadBestVariationsForFirstNProducts(tileListItems: TileListItem[], limit: number): Promise<ProductItem[]> {
        if (!tileListItems.length || limit <= 0) return [];

        const productItems = tileListItems.filter(isProductItem);
        if (!productItems.length) return [];

        const slice = productItems.slice(0, limit);
        const variationIds = Array.from(
            new Set(slice.map((item) => item.product.bestVariationId).filter((id): id is string => !!id)),
        );

        if (!variationIds.length) return slice;

        return this.loadVariationsForProducts(slice, variationIds);
    }

    /**
 *
 *
 *
 */
    async loadVariation(productItem: ProductItem, variationId: string): Promise<ProductItem> {
        if (!productItem || !variationId) {
            return productItem;
        }

        if (VariationLoadingService.alreadyLoaded(productItem, variationId)) {
            return {
                ...productItem,
                currentVariationId: variationId,
            };
        }

        /*            */
        const variationResult = await this.client.getVariation(variationId);

        if (variationResult.state === 'error') {
            return { ...productItem, variationLoadFailed: true };
        }

        const variation = variationResult.payload;
        const isBestVariation = variationId === productItem.product.bestVariationId;

        return VariationLoadingService.withLoadedVariation(productItem, variation, isBestVariation);
    }

    /**
 *
 *
 *
 */
    async loadVariationsForProducts(productItems: ProductItem[], variationIds: string[]): Promise<ProductItem[]> {
        if (!variationIds.length) {
            return productItems;
        }

        const variationsResult = await this.client.getVariations(variationIds);

        if (variationsResult.state === 'error') {
            return productItems.map((item) => ({ ...item, variationLoadFailed: true }));
        }

        const variations = variationsResult.payload;
        const variationMap = new Map(variations.map((v) => [v.variationId, v]));

        return productItems.map((productItem) => {
            const variationId = productItem.product.bestVariationId;
            if (!variationId) return productItem;

            const variation = variationMap.get(variationId);
            if (!variation) return productItem;

            const isBestVariation = true;
            return VariationLoadingService.withLoadedVariation(productItem, variation, isBestVariation);
        });
    }

    /**
 *
 *
 *
 */
    async loadVariations(variationIds: string[]): Promise<Variation[]> {
        if (!variationIds.length) return [];

        const variationsResult = await this.client.getVariations(variationIds);
        return variationsResult.state === 'success' ? variationsResult.payload : [];
    }

    /**
 *
 *
 */
    static withLoadedVariation(productItem: ProductItem, variation: Variation, isBestVariation: boolean): ProductItem {
        return {
            ...productItem,
            variations: [...productItem.variations, variation],
            currentVariationId: variation.variationId,
            colors: isBestVariation
                ? sortedColorsWithBestVariationFirst(variation.colors, variation.variationId)
                : productItem.colors,
        };
    }

    static alreadyLoaded(productItem: ProductItem, variationId: string): boolean {
        return productItem.variations.some((v) => v.variationId === variationId);
    }
}
